import { useState, useRef, useCallback } from 'react';
import type { Parameter } from '@shared/types';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandList,
  CommandItem,
} from '@/components/ui/command';
import { validateCodeExpression } from '@/utils/parameters/codeSubstitution';
import { AlertTriangle, Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CodeInputProps {
  param: Parameter;
  onCommit: (value: string) => void;
  onChange?: (value: string) => void;
  /** All parameters in the current SCAD file — used for identifier validation */
  allParameters?: Parameter[];
  /** Raw SCAD source — used to detect loop contexts and suppress false positives */
  scadCode?: string;
}

/**
 * Single-line code expression input for `code` parameters.
 *
 * When `param.options` is present, renders as a combobox: the user can either
 * select a preset expression from a dropdown or type their own freely.
 *
 * In both modes, unknown identifiers are flagged with a yellow warning icon
 * unless the parameter is used inside a `for` loop.
 */
export function CodeInput({
  param,
  onCommit,
  onChange,
  allParameters = [],
  scadCode = '',
}: CodeInputProps) {
  const [localValue, setLocalValue] = useState(String(param.value));
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasOptions = param.options && param.options.length > 0;

  // Keep local value in sync when param.value changes externally
  const prevValueRef = useRef(param.value);
  if (prevValueRef.current !== param.value) {
    prevValueRef.current = param.value;
    setLocalValue(String(param.value));
  }

  const unknownIdentifiers = validateCodeExpression(
    localValue,
    param.name,
    scadCode,
    allParameters,
  );
  const hasWarning = unknownIdentifiers.length > 0;

  const warningTooltip =
    unknownIdentifiers.length === 1
      ? `Unknown variable: ${unknownIdentifiers[0]}`
      : `Unknown variables: ${unknownIdentifiers.join(', ')}`;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Strip newlines — single-line only
      const raw = e.target.value.replace(/[\r\n]/g, '');
      setLocalValue(raw);
      onChange?.(raw);
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    onCommit(localValue);
  }, [localValue, onCommit]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        inputRef.current?.blur();
      }
    },
    [],
  );

  const handleSelectOption = useCallback(
    (expression: string) => {
      setLocalValue(expression);
      onChange?.(expression);
      onCommit(expression);
      setOpen(false);
      // Return focus to input after selection
      setTimeout(() => inputRef.current?.focus(), 0);
    },
    [onChange, onCommit],
  );

  // Build the input class string — right padding accommodates icons on the right
  const inputClass = cn(
    'flex h-8 w-full rounded-md border bg-transparent px-2 py-1 font-mono text-xs',
    'ring-offset-background placeholder:text-muted-foreground',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:cursor-not-allowed disabled:opacity-50',
    hasWarning
      ? 'border-yellow-500/60 dark:border-yellow-400/60'
      : 'border-input',
    // Reserve right-side space: warning icon (14px) + optional chevron button (28px) + gaps
    hasOptions && hasWarning ? 'pr-14' : hasOptions || hasWarning ? 'pr-8' : 'pr-2',
  );

  const warningIcon = hasWarning && (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="pointer-events-auto cursor-default flex-shrink-0">
          <AlertTriangle
            className="h-3.5 w-3.5 text-yellow-500 dark:text-yellow-400"
            aria-label={warningTooltip}
          />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        {warningTooltip}
      </TooltipContent>
    </Tooltip>
  );

  if (!hasOptions) {
    // ── Plain code input (no presets) ─────────────────────────────────────
    return (
      <div className="relative flex items-center w-full">
        <input
          ref={inputRef}
          type="text"
          id={param.name}
          name={param.name}
          autoComplete="off"
          spellCheck={false}
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className={inputClass}
        />
        {hasWarning && (
          <div className="absolute right-2 flex items-center pointer-events-none">
            {warningIcon}
          </div>
        )}
      </div>
    );
  }

  // ── Combobox variant (with preset options) ─────────────────────────────
  const selectedOption = param.options!.find(
    (opt) => String(opt.value) === localValue,
  );

  return (
    <div className="relative flex items-center w-full">
      {/* Free-text input */}
      <input
        ref={inputRef}
        type="text"
        id={param.name}
        name={param.name}
        autoComplete="off"
        spellCheck={false}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={inputClass}
      />

      {/* Absolute overlay for icons on the right side of the input */}
      <div className="absolute right-0 flex items-center pr-1 gap-0.5 pointer-events-none">
        {/* Warning icon (pointer-events re-enabled for tooltip) */}
        {hasWarning && (
          <div className="pointer-events-auto">
            {warningIcon}
          </div>
        )}

        {/* Chevron button that opens the preset dropdown */}
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Show preset expressions"
              aria-expanded={open}
              className={cn(
                'pointer-events-auto flex h-6 w-6 items-center justify-center rounded',
                'text-muted-foreground hover:text-foreground hover:bg-accent',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
                'transition-colors',
                open && 'bg-accent text-foreground',
              )}
              // Prevent the input's onBlur from firing and committing before
              // the popover opens (mousedown fires before blur)
              onMouseDown={(e) => e.preventDefault()}
            >
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform duration-150',
                  open && 'rotate-180',
                )}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            className="w-auto min-w-[160px] max-w-xs p-0"
            align="end"
            sideOffset={4}
            // Keep focus on the input when the popover opens
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command>
              <CommandList>
                {param.options!.map((opt) => {
                  const isSelected =
                    String(opt.value) === localValue ||
                    (selectedOption && opt.value === selectedOption.value);
                  return (
                    <CommandItem
                      key={String(opt.value)}
                      value={String(opt.value)}
                      onSelect={() => handleSelectOption(String(opt.value))}
                      className="font-mono text-xs gap-2 cursor-pointer"
                    >
                      <Check
                        className={cn(
                          'h-3.5 w-3.5 flex-shrink-0',
                          isSelected ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <div className="flex flex-col min-w-0">
                        {/* Label shown in a readable font when different from value */}
                        {opt.label !== String(opt.value) && (
                          <span className="font-sans text-xs text-foreground leading-tight">
                            {opt.label}
                          </span>
                        )}
                        <span className="font-mono text-xs text-muted-foreground truncate">
                          {String(opt.value)}
                        </span>
                      </div>
                    </CommandItem>
                  );
                })}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
